import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI } from '../services/api';
import { getDiceBearUserAvatar } from '../services/dicebear';
import { confirmAction } from '../utils/confirmationDialog';
import { BUTTON_COLORS } from '../utils/buttonColors';
import { createSortaFriendsClickHandler } from '../utils/navigation';
// import { formatRoleDisplay } from '../utils/role'; // ignore roles while beta testing

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sharedActivity, setSharedActivity] = useState(null);
  const [sharedActivityLoading, setSharedActivityLoading] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoading(true);
        const response = await userAPI.getUser(userId);
        setUser(response.data);
      } catch (err) {
        setError('Failed to load user');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [userId]);

  useEffect(() => {
    const loadSharedActivity = async () => {
      // Only load shared activity if user is loaded
      if (!user) {
        return;
      }

      try {
        setSharedActivityLoading(true);
        const response = await userAPI.getUserSharedActivity(userId);
        setSharedActivity(response.data);
      } catch (err) {
        console.error('Failed to load shared activity:', err);
        // Don't set error state for shared activity failures
      } finally {
        setSharedActivityLoading(false);
      }
    };

    loadSharedActivity();
  }, [user, userId]);

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleLogout = async () => {
    const confirmed = await confirmAction(
      'Log Out',
      'Are you sure you want to log out?',
      {
        confirmText: 'Log Out',
        cancelText: 'Cancel',
        confirmButtonColor: BUTTON_COLORS.LOGOUT,
        cancelButtonColor: BUTTON_COLORS.CANCEL,
      }
    );
    if (!confirmed) return;
    localStorage.removeItem('token');
    window.dispatchEvent(new Event('logout'));
    navigate('/login');
  };

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  if (error) {
    return (
      <div className="container">
        <div style={{ color: 'red', marginBottom: '20px' }}>{error}</div>
        <button onClick={handleBack} className="btn">Back to Dashboard</button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container">
        <div style={{ color: 'red', marginBottom: '20px' }}>User not found</div>
        <button onClick={handleBack} className="btn">Back to Dashboard</button>
      </div>
    );
  }

  const renderLastLogin = () => {
    if (!user.last_login) return '—';
    try {
      const today = new Date();
      const last = new Date(user.last_login + 'T00:00:00');
      const diffMs = today.setHours(0,0,0,0) - last.getTime();
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (days <= 0) return 'today';
      if (days === 1) return '1 day ago';
      return `${days} days ago`;
    } catch (e) {
      return user.last_login;
    }
  };

  const renderSocials = () => {
    if (!user.socials || user.socials.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: '15px' }}>
        <h4 style={{ marginBottom: '10px', color: 'var(--color-text)' }}>Socials</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {user.socials.map((social, index) => {
            const isTelegram = social.platform === 'telegram';
            const icon = isTelegram ? '✈️' : '📱';
            const name = isTelegram ? 'Telegram' : 'Signal';
            
            return (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span aria-hidden>{icon}</span>
                <span style={{ fontWeight: 'bold', color: 'var(--color-text)' }}>{name}:</span>
                {social.link ? (
                  <a 
                    href={social.link} 
                    target="_blank" 
                    rel="noreferrer"
                    style={{ 
                      color: 'var(--color-primary)', 
                      textDecoration: 'none',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    {social.value}
                  </a>
                ) : (
                  <span style={{ color: 'var(--color-text)' }}>{social.value}</span>
                )}
                {social.is_verified && (
                  <span style={{ color: 'var(--color-success)', fontSize: '12px' }}>✓</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSharedActivity = () => {
    // Only show if user is loaded
    if (!user) {
      return null;
    }

    if (sharedActivityLoading) {
      return (
        <div style={{ marginTop: '20px' }}>
          <h4 style={{ marginBottom: '10px', color: 'var(--color-text)' }}>Shared Activity</h4>
          <div style={{ color: 'var(--color-text-muted)' }}>Loading...</div>
        </div>
      );
    }

    // Show the section even if no data (will show 0s)
    const friendStacksTotal = sharedActivity ? 
      (sharedActivity.friend_stacks_shared + sharedActivity.friend_twisted_stacks_shared) : 0;
    const groupStacksTotal = sharedActivity ? 
      (sharedActivity.group_stacks_shared + sharedActivity.group_twisted_stacks_shared) : 0;


    return (
      <div style={{ marginTop: '20px' }}>
        <h4 style={{ marginBottom: '10px', color: 'var(--color-text)' }}>Shared Activity</h4>
        <div style={{ 
          border: '1px solid var(--color-border)', 
          borderRadius: '8px', 
          padding: '12px',
          backgroundColor: 'var(--color-surface)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ 
                  padding: '8px 0', 
                  borderBottom: '1px solid var(--color-border)',
                  fontWeight: '500',
                  color: 'var(--color-text)'
                }}>
                  Friend Stacks
                </td>
                <td style={{ 
                  padding: '8px 0', 
                  borderBottom: '1px solid var(--color-border)',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: 'var(--color-text)'
                }}>
                  {friendStacksTotal}
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '8px 0',
                  fontWeight: '500',
                  color: 'var(--color-text)'
                }}>
                  Group Stacks
                </td>
                <td style={{ 
                  padding: '8px 0',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: 'var(--color-text)'
                }}>
                  {groupStacksTotal}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      <header className="header">
        <div className="container">
          <div className="header-content">
            <h1 style={{ cursor: 'pointer' }} onClick={createSortaFriendsClickHandler(navigate)}>SortaFriends</h1>
            <button
              onClick={handleLogout}
              className="notification-icon"
              title="Logout"
              aria-label="Logout"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="currentColor">
                <path d="M10 17l1.41-1.41L8.83 13H20v-2H8.83l2.58-2.59L10 7l-5 5 5 5z"/>
                <path d="M4 19h2v2H4c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2h2v2H4v14z"/>
              </svg>
            </button>
          </div>
        </div>
      </header>

      <div className="container">
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <img 
              src={getDiceBearUserAvatar(user.username, user.avatar_id)} 
              alt={`${user.username} avatar`}
              style={{ width: '100px', height: '100px', borderRadius: '50%' }}
            />
                          <div>
                <h2>{user.username}</h2>
                {user.bio && (
                  <p style={{ fontStyle: 'italic', color: '#495057', marginBottom: '10px' }}>
                    "{user.bio}"
                  </p>
                )}
                {/* ignore roles while beta testing */}
                {/* <p>
                  <strong>Role:</strong> <strong>{formatRoleDisplay(user.role)}</strong>
                </p> */}
                <p>
                  <strong>Last Login:</strong> <strong>{renderLastLogin()}</strong>
                </p>
                {renderSocials()}
                {renderSharedActivity()}
              </div>
          </div>
          <div style={{ marginTop: '20px' }}>
            <button onClick={handleBack} className="btn">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile; 